import { Box, Container, Typography, Button, Grid } from "@mui/material";
import { Link } from "react-router-dom";
import { useDocumentTitle } from "../../utils/data";
import { keyframes } from "@mui/system";

const floatBall = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(20px, -20px); }
  50% { transform: translate(-10px, 20px); }
  75% { transform: translate(10px, -10px); }
`;

const scaleInCenter = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to { opacity: 1; transform: scale(1); }
`;

const sportReserveFeatures = [
  {
    icon: "🏟️",
    title: "Venue Management",
    desc: "Manage multiple venues, facilities, and courts with ease. Track availability and optimize space utilization."
  },
  {
    icon: "📅",
    title: "Smart Scheduling",
    desc: "Automated booking system that prevents conflicts and maximizes your facility usage efficiency."
  },
  {
    icon: "👥",
    title: "Member Portal",
    desc: "Allow members to book, manage reservations, and access their activity history anytime, anywhere."
  },
  {
    icon: "💳",
    title: "Payment Processing",
    desc: "Secure, integrated payment solutions for seamless transactions and subscription management."
  },
  {
    icon: "📊",
    title: "Analytics & Insights",
    desc: "Real-time reporting on bookings, revenue, and member engagement to drive business growth."
  },
  {
    icon: "🔔",
    title: "Notifications & Alerts",
    desc: "Automated reminders, confirmations, and notifications keep members engaged and reduce no-shows."
  }
];

const SportReserve = () => {
  useDocumentTitle("Briozon - Sport Reserve");

  return (
    <Box>
      {/* Hero Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Floating decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
            animation: `${floatBall} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: "3%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(118,75,162,0.1) 0%, rgba(91,124,250,0.1) 100%)",
            animation: `${floatBall} 6s ease-in-out infinite 1s`,
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: { xs: "12px", sm: "14px" },
                    fontWeight: 700,
                    color: "#5b7cfa",
                    letterSpacing: "2px",
                    mb: 1,
                    display: "block",
                  }}
                >
                  SPORTS MANAGEMENT
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "32px", sm: "42px", md: "48px" },
                    fontWeight: 800,
                    color: "#1b1f33",
                    lineHeight: 1.2,
                    mb: 2,
                  }}
                >
                  Sport{" "}
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Reserve
                  </Box>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "16px", sm: "19px", md: "20px" },
                    color: "#4c5472",
                    lineHeight: 1.8,
                    mb: 3,
                    textAlign: "justify",
                  }}
                >
                  Streamline your sports facility management with our comprehensive reservation and booking platform. 
                  Maximize venue utilization, simplify member management, and boost revenue with intelligent scheduling.
                </Typography>
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    color: "#fff",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    fontSize: "16px",
                    textTransform: "none",
                    boxShadow: "0 8px 24px rgba(91, 124, 250, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.4)",
                    },
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Grid>

            {/* Right - Creative Visual */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 320, sm: 400, md: 450 },
                  animation: `${scaleInCenter} 0.8s ease-out 0.2s both`,
                }}
              >
                {/* Main gradient circle */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 220, sm: 280, md: 320 },
                    height: { xs: 220, sm: 280, md: 320 },
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
                    filter: "blur(40px)",
                  }}
                />

                {/* Central icon */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 100, sm: 120 },
                    height: { xs: 100, sm: 120 },
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "48px", sm: "56px" },
                    boxShadow: "0 20px 40px rgba(91, 124, 250, 0.4)",
                    animation: `${floatBall} 6s ease-in-out infinite`,
                    zIndex: 2,
                  }}
                >
                  🏟️
                </Box>

                {/* Floating feature cards */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "8%",
                    left: "10%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 7s ease-in-out infinite 0.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>📅</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Smart Booking
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "15%",
                    right: "5%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 8s ease-in-out infinite 1s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>💰</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Revenue
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "20%",
                    left: "5%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 9s ease-in-out infinite 1.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>👥</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Members
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "10%",
                    right: "10%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 6s ease-in-out infinite 2s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>📱</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Mobile App
                  </Typography>
                </Box>

                {/* Decorative rings */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 200, sm: 260 },
                    height: { xs: 200, sm: 260 },
                    borderRadius: "50%",
                    border: "2px dashed rgba(91, 124, 250, 0.2)",
                    animation: `${floatBall} 10s ease-in-out infinite`,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 280, sm: 360 },
                    height: { xs: 280, sm: 360 },
                    borderRadius: "50%",
                    border: "1px dashed rgba(118, 75, 162, 0.15)",
                    animation: `${floatBall} 12s ease-in-out infinite reverse`,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* === SECTION 2: Features Bento === */}
      <Box
        sx={{
          py: { xs: 7, sm: 9, md: 12 },
          background: "linear-gradient(160deg, #eff6ff 0%, #ffffff 50%, #f0f9ff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle, rgba(59,130,246,0.07) 1px, transparent 1px)", backgroundSize: "38px 38px", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", top: "5%", right: "5%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: "5%", left: "5%", width: 240, height: 240, borderRadius: "50%", background: "radial-gradient(circle, rgba(56,189,248,0.09) 0%, transparent 70%)", pointerEvents: "none" }} />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", background: "linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(56,189,248,0.1) 100%)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: "50px", px: 2.5, py: 0.8, mb: 2.5 }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#1d4ed8", letterSpacing: "2px", textTransform: "uppercase" }}>🏟️ Platform Features</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontSize: { xs: "30px", sm: "38px", md: "46px" }, fontWeight: 800, color: "#1b1f33", lineHeight: 1.2, mb: 2 }}>
              Complete Facility{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #3b82f6 0%, #38bdf8 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Management Solution
              </Box>
            </Typography>
            <Typography sx={{ fontSize: { xs: "16px", sm: "18px" }, color: "#4c5472", maxWidth: 580, mx: "auto", lineHeight: 1.8 }}>
              Everything you need to manage your sports facility, bookings, members, and revenue in one integrated platform 🎯
            </Typography>
          </Box>

          {/* Bento Row 1: Hero feature + live stats */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={7} sx={{ display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  borderRadius: "24px",
                  background: "linear-gradient(135deg, #3b82f6 0%, #38bdf8 100%)",
                  p: { xs: 4, sm: 5 },
                  minHeight: { xs: "auto", md: 310 },
                  position: "relative",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  flex: 1,
                }}
              >
                <Box sx={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.08)", pointerEvents: "none" }} />
                <Box sx={{ position: "absolute", top: 20, left: 20, width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.06)", pointerEvents: "none" }} />
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, background: "rgba(255,255,255,0.2)", backdropFilter: "blur(8px)", borderRadius: "50px", px: 2, py: 0.7, mb: 3, alignSelf: "flex-start", zIndex: 1 }}>
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#fff", letterSpacing: "1.5px" }}>⚡ SMART SCHEDULING</Typography>
                </Box>
                <Box sx={{ fontSize: "60px", mb: 2, filter: "drop-shadow(0 8px 20px rgba(0,0,0,0.18))", zIndex: 1 }}>📅</Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: "#fff", mb: 1.5, fontSize: { xs: "24px", sm: "30px" }, zIndex: 1 }}>
                  Zero-Conflict Booking Engine
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.92)", lineHeight: 1.75, fontSize: "16px", maxWidth: 460, zIndex: 1 }}>
                  Our intelligent scheduling system automatically prevents double-bookings, manages time slots, and maximizes facility usage with real-time availability across all courts and fields.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={5} sx={{ display: "flex", flexDirection: "column" }}>
              <Box sx={{ borderRadius: "24px", background: "#fff", border: "1px solid rgba(59,130,246,0.15)", boxShadow: "0 4px 24px rgba(59,130,246,0.08)", p: { xs: 4, sm: 4.5 }, flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1b1f33", mb: 3, fontSize: "18px" }}>📊 Live Performance Metrics</Typography>
                {[
                  { label: "Booking Conflicts Prevented", value: "99.8%", icon: "🛡️", color: "#3b82f6" },
                  { label: "Average Utilization Rate", value: "+42%", icon: "📈", color: "#38bdf8" },
                  { label: "Member Satisfaction Score", value: "4.9 ★", icon: "⭐", color: "#f59e0b" },
                  { label: "Average Revenue Growth", value: "+35%", icon: "💰", color: "#6366f1" },
                ].map((stat) => (
                  <Box key={stat.label} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: "12px", background: `${stat.color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", flexShrink: 0 }}>{stat.icon}</Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: "12px", color: "#6b7280", fontWeight: 500, mb: 0.3 }}>{stat.label}</Typography>
                      <Typography sx={{ fontSize: "18px", fontWeight: 800, color: stat.color }}>{stat.value}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* Bento Row 2: 6 feature cards */}
          <Grid container spacing={3}>
            {sportReserveFeatures.map((item, idx) => {
              const accents = ["#3b82f6", "#38bdf8", "#6366f1", "#f59e0b", "#ec4899", "#3b82f6"];
              const accent = accents[idx % accents.length];
              return (
                <Grid item xs={12} sm={6} md={4} key={idx} sx={{ display: "flex", flexDirection: "column" }}>
                  <Box
                    sx={{
                      borderRadius: "20px",
                      background: "#fff",
                      border: "1px solid rgba(59,130,246,0.12)",
                      p: { xs: 3, sm: 3.5 },
                      flex: 1,
                      transition: "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
                      position: "relative",
                      overflow: "hidden",
                      zIndex: 0,
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: `0 20px 48px ${accent}28`,
                        borderColor: `${accent}44`,
                        zIndex: 2,
                      },
                      "&:hover .feat-icon": {
                        transform: "scale(1.12) rotate(-5deg)",
                      },
                    }}
                  >
                    <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: `linear-gradient(90deg, ${accent} 0%, transparent 100%)`, borderRadius: "20px 20px 0 0" }} />
                    <Box
                      className="feat-icon"
                      sx={{
                        width: 56, height: 56, borderRadius: "14px",
                        background: `${accent}18`,
                        border: `1px solid ${accent}30`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "1.7rem", mb: 2,
                        transition: "transform 0.35s cubic-bezier(0.34,1.56,0.64,1)",
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#1b1f33", mb: 1, fontSize: { xs: "18px", sm: "20px" } }}>{item.title}</Typography>
                    <Typography sx={{ color: "#4c5472", lineHeight: 1.7, fontSize: "15px" }}>{item.desc}</Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* === SECTION 3: How It Works === */}
      <Box sx={{ py: { xs: 7, sm: 9, md: 11 }, background: "#fff", position: "relative", overflow: "hidden" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", background: "linear-gradient(135deg, rgba(59,130,246,0.1), rgba(56,189,248,0.1))", border: "1px solid rgba(59,130,246,0.25)", borderRadius: "50px", px: 2.5, py: 0.8, mb: 2.5 }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#1d4ed8", letterSpacing: "2px" }}>🚀 GET STARTED IN MINUTES</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontSize: { xs: "30px", sm: "38px", md: "44px" }, fontWeight: 800, color: "#1b1f33", mb: 2 }}>
              Up and Running in{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #3b82f6 0%, #38bdf8 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>3 Simple Steps</Box>
            </Typography>
            <Typography sx={{ fontSize: { xs: "16px", sm: "18px" }, color: "#4c5472", maxWidth: 500, mx: "auto", lineHeight: 1.8 }}>
              No tech expertise needed. Set up your facility and start taking bookings the same day.
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 3, md: 4 }}>
            {[
              { num: "01", icon: "🏟️", title: "Set Up Your Venue", body: "Add your facilities, courts, fields, and time slots. Customize pricing, rules, and member access in minutes with our intuitive dashboard.", color: "#3b82f6" },
              { num: "02", icon: "📱", title: "Members Book Online", body: "Members access your branded portal or mobile app to browse availability, book slots, and pay — any time, from any device.", color: "#38bdf8" },
              { num: "03", icon: "📊", title: "Manage & Grow", body: "Track bookings, revenue, and usage patterns in real time. Automate reminders, handle cancellations, and unlock insights to grow smarter.", color: "#6366f1" },
            ].map((step) => (
              <Grid item xs={12} md={4} key={step.num} sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    borderRadius: "24px",
                    background: `linear-gradient(160deg, ${step.color}0d 0%, #fff 60%)`,
                    border: `1px solid ${step.color}22`,
                    p: { xs: 4, sm: 5 },
                    flex: 1,
                    position: "relative",
                    overflow: "hidden",
                    zIndex: 0,
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": { transform: "translateY(-8px)", boxShadow: `0 20px 50px ${step.color}22`, zIndex: 2 },
                  }}
                >
                  <Typography sx={{ position: "absolute", top: -10, right: 20, fontSize: "100px", fontWeight: 900, color: `${step.color}12`, fontFamily: "monospace", lineHeight: 1, userSelect: "none" }}>{step.num}</Typography>
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.8, background: `${step.color}18`, borderRadius: "50px", px: 1.5, py: 0.5, mb: 3 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: step.color }} />
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: step.color, letterSpacing: "1px" }}>STEP {step.num}</Typography>
                  </Box>
                  <Box sx={{ fontSize: "52px", mb: 2, filter: `drop-shadow(0 4px 14px ${step.color}44)` }}>{step.icon}</Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#1b1f33", mb: 1.5, fontSize: { xs: "20px", sm: "22px" } }}>{step.title}</Typography>
                  <Typography sx={{ color: "#4c5472", lineHeight: 1.75, fontSize: "15px" }}>{step.body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* === SECTION 4: Venue Types === */}
      <Box sx={{ py: { xs: 7, sm: 9, md: 11 }, background: "linear-gradient(160deg, #eff6ff 0%, #f0f9ff 100%)", position: "relative", overflow: "hidden" }}>
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle, rgba(59,130,246,0.06) 1px, transparent 1px)", backgroundSize: "36px 36px", pointerEvents: "none" }} />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center", mb: { xs: 6, md: 7 } }}>
            <Box sx={{ display: "inline-flex", background: "linear-gradient(135deg, rgba(59,130,246,0.1), rgba(56,189,248,0.1))", border: "1px solid rgba(59,130,246,0.25)", borderRadius: "50px", px: 2.5, py: 0.8, mb: 2.5 }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#1d4ed8", letterSpacing: "2px" }}>🌍 BUILT FOR EVERY SPORT</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontSize: { xs: "28px", sm: "36px", md: "42px" }, fontWeight: 800, color: "#1b1f33", mb: 2 }}>
              Works for{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #3b82f6 0%, #38bdf8 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>Every Venue Type</Box>
            </Typography>
            <Typography sx={{ fontSize: { xs: "16px", sm: "17px" }, color: "#4c5472", maxWidth: 520, mx: "auto", lineHeight: 1.8 }}>
              Whether you run a tennis club, a gym, or a multi-sport complex — Sport Reserve adapts to your workflow.
            </Typography>
          </Box>

          <Grid container spacing={2.5}>
            {[
              { icon: "🎾", label: "Tennis & Racquet Sports", desc: "Court booking, lesson scheduling, tournament management" },
              { icon: "🏊", label: "Swimming Pools & Aquatics", desc: "Lane reservations, swim classes, lifeguard scheduling" },
              { icon: "⚽", label: "Football & Multi-Sport", desc: "Pitch allocation, team registrations, league management" },
              { icon: "🏋️", label: "Fitness & Gyms", desc: "Class bookings, personal training, membership management" },
              { icon: "🎳", label: "Recreational Centers", desc: "Multi-activity booking, event hosting, guest passes" },
              { icon: "🏌️", label: "Golf Courses", desc: "Tee time bookings, caddy assignments, clubhouse access" },
            ].map((venue) => (
              <Grid item xs={12} sm={6} md={4} key={venue.label} sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    borderRadius: "18px",
                    background: "#fff",
                    border: "1px solid rgba(59,130,246,0.12)",
                    p: 3,
                    flex: 1,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    zIndex: 0,
                    transition: "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
                    "&:hover": { transform: "translateY(-6px)", boxShadow: "0 16px 40px rgba(59,130,246,0.12)", borderColor: "rgba(59,130,246,0.3)", zIndex: 2 },
                  }}
                >
                  <Box sx={{ width: 52, height: 52, borderRadius: "14px", background: "linear-gradient(135deg, rgba(59,130,246,0.12) 0%, rgba(56,189,248,0.12) 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px", flexShrink: 0 }}>{venue.icon}</Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: "#1b1f33", mb: 0.5, fontSize: { xs: "16px", sm: "17px" } }}>{venue.label}</Typography>
                    <Typography sx={{ color: "#6b7280", fontSize: "14px", lineHeight: 1.6 }}>{venue.desc}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box
        sx={{
          py: { xs: 8, sm: 10, md: 12 },
          background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: "absolute",
            top: "-50px",
            right: "-50px",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-30px",
            left: "-30px",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />

        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                fontWeight: 800,
                color: "#fff",
                mb: 2,
              }}
            >
              Ready to Transform Your Facility?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                color: "rgba(255,255,255,0.9)",
                mb: 4,
                maxWidth: "700px",
                mx: "auto",
                lineHeight: 1.8,
              }}
            >
              Discover how Sport Reserve can streamline your operations, increase member satisfaction, and boost revenue. 
              Contact us today for a personalized demo and see the difference our platform can make.
            </Typography>
            <Button
              component="a"
              href="/contact"
              variant="contained"
              sx={{
                backgroundColor: "#fff",
                color: "#5b7cfa",
                fontWeight: 700,
                px: 5,
                py: 1.5,
                borderRadius: "12px",
                fontSize: "16px",
                textTransform: "none",
                transition: "all 0.3s ease",
                boxShadow: "0 8px 24px rgba(91, 124, 250, 0.08)",
                "&:hover": {
                  backgroundColor: "#f0f0f0",
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 24px rgba(0,0,0,0.12)",
                },
              }}
            >
              Let's Connect
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default SportReserve;
